import { customElement, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { route } from '@aurelia/router';
import { I18N, Signals } from '@aurelia/i18n';
import template from './app.html';
import { GalleryView } from './views/gallery-view';
import { TagsList } from './views/tags-list';
import { SettingsView, THEME_CHANGED_CHANNEL } from './views/settings';
import { StatisticsView } from './views/statistics-view';
import { MaintenanceView } from './views/maintenance-view';
import { AppUpdatesView } from './views/app-updates-view';
import { ImportProtocolsList } from './views/import-protocols-list';
import { RulesList } from './views/rules-list';
import { PersonsList } from './views/persons-list';
import { PersonFaceRatings } from './views/person-face-ratings';
import { CollectionsView } from './views/collections-view';
import { UsersList } from './views/users-list';
import { HelpView } from './views/help-view';
import { getMe, getUserSettings, upsertUserSetting } from './services/api.service';
import { LANGUAGE_CHANGED_CHANNEL, LANGUAGES } from './services/languages';
import { PERMISSIONS_CHANGED_CHANNEL, setPermissions } from './services/permissions.service';
import { setDateFormat, type DateFormatOption } from './services/date-format.service';
import { setPersonPickerSplitViewEnabled } from './services/person-picker-settings.service';
import { setDefaultVisibility } from './services/default-visibility.service';
import { setShowFamilyMedia } from './services/media-scope.service';
import { setAgeAutoThresholdYears } from './services/person-age-settings.service';
import {
  getMediaDisplayQuality,
  MEDIA_DISPLAY_QUALITY_CHANGED_CHANNEL,
  setMediaDisplayQuality,
  type MediaDisplayQuality,
} from './services/media-display-quality.service';
import { apiUrl } from './services/runtime-config';
import { applicationDisplayVersion } from './services/application-version';

@route({
  routes: [
    { path: '', redirectTo: 'gallery' },
    { path: 'gallery', component: GalleryView, title: 'Gallery' },
    { path: 'collections', component: CollectionsView, title: 'Collections' },
    { path: 'collections/:id', component: CollectionsView, title: 'Collections' },
    { path: 'tags', component: TagsList, title: 'Tags' },
    { path: 'persons/face-ratings', component: PersonFaceRatings, title: 'Face ratings' },
    { path: 'persons/:id/face-ratings', component: PersonFaceRatings, title: 'Face ratings' },
    { path: 'persons', component: PersonsList, title: 'Persons' },
    { path: 'statistics', component: StatisticsView, title: 'Statistics' },
    { path: 'maintenance', component: MaintenanceView, title: 'Maintenance' },
    { path: 'app-updates', component: AppUpdatesView, title: 'App updates' },
    { path: 'users', component: UsersList, title: 'Users' },
    { path: 'import-protocols', component: ImportProtocolsList, title: 'Import Protocols' },
    { path: 'rules', component: RulesList, title: 'Rules' },
    { path: 'settings', component: SettingsView, title: 'Settings' },
    { path: 'help', component: HelpView, title: 'Help' },
  ],
})
@customElement({ name: 'app', template })
export class App {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _localeSub: IDisposable | null = null;
  private _themeSub: IDisposable | null = null;

  public sidebarCollapsed = false;
  public language = localStorage.getItem('radish-language') ?? 'en';
  public theme: 'light' | 'dark' = (localStorage.getItem('radish-theme') as 'light' | 'dark') ?? 'light';
  public mediaDisplayQuality: MediaDisplayQuality = getMediaDisplayQuality();
  public showLangMenu = false;
  public showUserMenu = false;
  public currentUsername = '';
  public currentDisplayName = '';
  public permissions: string[] = [];
  public isReady = false;
  public readonly applicationDisplayVersion = applicationDisplayVersion;

  public readonly languages = LANGUAGES;

  public get currentCountryCode(): string {
    return this.languages.find(language => language.value === this.language)?.countryCode ?? 'us';
  }

  public async binding(): Promise<void> {
    try {
      const statusRes = await fetch(apiUrl('/auth/status'), { credentials: 'include' });
      const status = await statusRes.json() as { authenticated: boolean };
      if (!status.authenticated) {
        window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`);
        return;
      }
    } catch {
      window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`);
      return;
    }

    this.isReady = true;

    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this.language = this.i18n.getLocale();
    });

    this._themeSub = this.ea.subscribe(THEME_CHANGED_CHANNEL, (theme: 'light' | 'dark') => {
      this.theme = theme;
    });

    void getMe()
      .then(response => {
        this.currentUsername = response.username;
        this.currentDisplayName = response.displayName ?? response.username;
        this.permissions = response.permissions ?? [];
        setPermissions(this.permissions);
        this.ea.publish(PERMISSIONS_CHANGED_CHANNEL);
        return getUserSettings();
      })
      .then(settings => {
        for (const setting of settings) {
          if (setting.key === 'theme') {
            const theme = setting.value as 'light' | 'dark';
            this.theme = theme;
            document.documentElement.setAttribute('data-theme', theme);
            localStorage.setItem('radish-theme', theme);
            this.ea.publish(THEME_CHANGED_CHANNEL, theme);
          }
          if (setting.key === 'language') {
            this.language = setting.value;
            localStorage.setItem('radish-language', setting.value);
            this.ea.publish(LANGUAGE_CHANGED_CHANNEL, setting.value);
            void this.i18n.setLocale(setting.value);
          }
          if (setting.key === 'dateFormat') {
            setDateFormat(setting.value as DateFormatOption);
          }
          if (setting.key === 'personPickerSplitViewEnabled') {
            setPersonPickerSplitViewEnabled(setting.value === 'true');
          }
          if (setting.key === 'defaultUploadVisibility') {
            setDefaultVisibility(setting.value === 'family' ? 'family' : 'private');
          }
          if (setting.key === 'showFamilyMedia') {
            setShowFamilyMedia(setting.value !== 'false');
          }
          if (setting.key === 'personAgeAutoThresholdYears') {
            const years = Number(setting.value);
            if (Number.isFinite(years) && years >= 0)
              setAgeAutoThresholdYears(years);
          }
          if (setting.key === 'mediaDisplayQuality') {
            const quality: MediaDisplayQuality = setting.value === 'medium' ? 'medium' : 'high';
            this.mediaDisplayQuality = quality;
            setMediaDisplayQuality(quality);
            this.ea.publish(MEDIA_DISPLAY_QUALITY_CHANGED_CHANNEL, quality);
          }
        }
      })
      .catch(() => {});
  }

  public unbinding(): void {
    this._localeSub?.dispose();
    this._themeSub?.dispose();
  }

  public toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  public async toggleTheme(): Promise<void> {
    const nextTheme: 'light' | 'dark' = this.theme === 'light' ? 'dark' : 'light';
    this.theme = nextTheme;
    document.documentElement.setAttribute('data-theme', nextTheme);
    localStorage.setItem('radish-theme', nextTheme);
    this.ea.publish(THEME_CHANGED_CHANNEL, nextTheme);
    await upsertUserSetting('theme', nextTheme).catch(() => {});
  }

  public async toggleMediaDisplayQuality(): Promise<void> {
    const nextQuality: MediaDisplayQuality = this.mediaDisplayQuality === 'high' ? 'medium' : 'high';
    this.mediaDisplayQuality = nextQuality;
    setMediaDisplayQuality(nextQuality);
    this.ea.publish(MEDIA_DISPLAY_QUALITY_CHANGED_CHANNEL, nextQuality);
    await upsertUserSetting('mediaDisplayQuality', nextQuality).catch(() => {});
  }

  public toggleLangMenu(): void {
    this.showLangMenu = !this.showLangMenu;
  }

  public closeLangMenuDelayed(): void {
    setTimeout(() => { this.showLangMenu = false; }, 150);
  }

  public toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  public closeUserMenuDelayed(): void {
    setTimeout(() => { this.showUserMenu = false; }, 150);
  }

  public logout(): void {
    window.location.href = apiUrl('/auth/logout');
  }

  public async changeLanguage(language: string): Promise<void> {
    this.language = language;
    this.showLangMenu = false;
    localStorage.setItem('radish-language', language);
    this.ea.publish(LANGUAGE_CHANGED_CHANNEL, language);
    void this.i18n.setLocale(language);
    await upsertUserSetting('language', language).catch(() => {});
  }

}
